/**
 * Captura PCM 16 kHz desde un MediaStream existente (AudioWorklet).
 * No pide getUserMedia: reutiliza el stream del AudioRecorder.
 */

const TARGET_SR = 16000
const FRAME_MS = 100

/**
 * @returns {string}
 */
function resolveWorkletUrl() {
  try {
    let path = '/voice/pcmCapture.worklet.js'
    try {
      const base =
        (typeof process !== 'undefined' &&
          process.env &&
          process.env.PUBLIC_URL) ||
        ''
      const prefix = String(base || '').replace(/\/+$/, '')
      path = `${prefix}/voice/pcmCapture.worklet.js`.replace(/\/{2,}/g, '/')
      if (!path.startsWith('/')) path = `/${path}`
    } catch (e) {
      path = '/voice/pcmCapture.worklet.js'
    }
    return new URL(path, window.location.href).href
  } catch (e) {
    return './voice/pcmCapture.worklet.js'
  }
}

/**
 * @returns {boolean}
 */
export function isPcmCaptureSupported() {
  try {
    return !!(
      typeof window !== 'undefined' &&
      (window.AudioContext || window.webkitAudioContext) &&
      typeof AudioWorkletNode !== 'undefined'
    )
  } catch (e) {
    return false
  }
}

export class PcmCaptureSession {
  /**
   * @param {{
   *   onFrame?: (pcm: Float32Array, sampleRate: number) => void,
   *   onError?: (err: Error) => void,
   * }} [opts]
   */
  constructor(opts = {}) {
    this.onFrame = opts.onFrame || null
    this.onError = opts.onError || null
    /** @type {AudioContext|null} */
    this._ctx = null
    /** @type {MediaStreamAudioSourceNode|null} */
    this._source = null
    /** @type {AudioWorkletNode|null} */
    this._node = null
    this._running = false
  }

  /**
   * @param {MediaStream} stream
   * @returns {Promise<boolean>}
   */
  async start(stream) {
    if (this._running) return true
    if (!isPcmCaptureSupported()) {
      throw new Error('AudioWorklet no soportado')
    }
    if (!stream) throw new Error('MediaStream vacío')

    const AudioCtx = window.AudioContext || window.webkitAudioContext
    const ctx = new AudioCtx({ sampleRate: TARGET_SR })
    // Algunos browsers ignoran sampleRate pedido; el worklet resamplea.
    this._ctx = ctx
    try {
      if (ctx.state === 'suspended') await ctx.resume()
    } catch (e) {
      /* ignore */
    }

    try {
      await ctx.audioWorklet.addModule(resolveWorkletUrl())
    } catch (e) {
      try {
        await ctx.close()
      } catch (e2) {
        /* ignore */
      }
      this._ctx = null
      throw e
    }

    const source = ctx.createMediaStreamSource(stream)
    const node = new AudioWorkletNode(ctx, 'udv-pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      processorOptions: {
        targetSampleRate: TARGET_SR,
        frameMs: FRAME_MS,
      },
    })

    node.port.onmessage = (ev) => {
      const msg = ev && ev.data
      if (!msg || msg.type !== 'frame' || !msg.pcm) return
      const pcm = new Float32Array(msg.pcm)
      if (this.onFrame) {
        try {
          this.onFrame(pcm, msg.sampleRate || TARGET_SR)
        } catch (e) {
          /* ignore */
        }
      }
    }

    const mute = ctx.createGain()
    mute.gain.value = 0
    source.connect(node)
    node.connect(mute)
    mute.connect(ctx.destination)

    this._source = source
    this._node = node
    this._running = true
    return true
  }

  pause() {
    try {
      if (this._node) this._node.port.postMessage({ type: 'pause' })
    } catch (e) {
      /* ignore */
    }
  }

  resume() {
    try {
      if (this._ctx && this._ctx.state === 'suspended') {
        void this._ctx.resume()
      }
      if (this._node) this._node.port.postMessage({ type: 'resume' })
    } catch (e) {
      /* ignore */
    }
  }

  async stop() {
    this._running = false
    try {
      if (this._node) {
        this._node.port.onmessage = null
        this._node.disconnect()
      }
    } catch (e) {
      /* ignore */
    }
    try {
      if (this._source) this._source.disconnect()
    } catch (e) {
      /* ignore */
    }
    this._node = null
    this._source = null
    if (this._ctx) {
      try {
        await this._ctx.close()
      } catch (e) {
        /* ignore */
      }
    }
    this._ctx = null
  }
}

export default PcmCaptureSession
